import { useCallback, useEffect, useState } from 'react'
import { Sparkles } from 'lucide-react'
import { PillSelect } from '../ui/PillSelect'

/**
 * Energy mode — the user's own declaration about today's capacity.
 *
 * Defect 2026-08-29: energy modes were decoration. The READ half was fully
 * live in production (the intake gate resolves the committed-unit ceiling from
 * the mode, the release coordinator loads it, the pacing policy branches on
 * it), but nothing could WRITE one — no route, no control — so the ladder
 * could never be climbed. This is the control.
 *
 * Two rules this component keeps:
 *   1. The mode is USER-AUTHORED. Nothing here reacts to an engine suggestion
 *      and nothing sets a mode on the user's behalf.
 *   2. It only says what the server actually reported. The unit numbers come
 *      from the server's ladder, the reset line renders only when the server
 *      returned an expiry, and nothing is claimed about what the mode will do
 *      beyond what the server says it governs (`governs_plan_size`).
 *
 * 2026-09-11: the five-button block became a pill (`EnergyModePill`) in the
 * masthead. Owner: the block "does make sense on /today", but as "a sweet
 * dropdown" — it had been hidden behind a "why?" disclosure, where a CONTROL
 * has no business being. The block component stays for any surface that wants
 * the long form; both share one hook.
 */

const ENDPOINT = '/api/rationing/v1/energy-mode'

interface EnergyModeOption {
  value: string
  core_units: number
  optional_units: number
}

export interface EnergyModeResponse {
  success: boolean
  mode: string | null
  baseline_mode: string | null
  effective_mode: string
  valid_until: string | null
  modes: EnergyModeOption[]
  /** True when the declared rung also sizes the core plan (the server's
   *  SCHEDULING_ENERGY_PLAN_SIZE flag). Absent on older servers = unknown,
   *  and the copy then claims only what is always true. */
  governs_plan_size?: boolean
}

/** Display names only — the server owns the vocabulary. */
const MODE_LABELS: Record<string, string> = {
  recovery: 'Recovery',
  gentle: 'Gentle',
  average: 'Average',
  momentum: 'Momentum',
  superhero: 'Superhero',
}

export function labelFor(value: string): string {
  return MODE_LABELS[value] ?? value.charAt(0).toUpperCase() + value.slice(1)
}

export function unitsWords(o: { core_units: number; optional_units: number }): string {
  return `${o.core_units} core${o.optional_units > 0 ? ` · ${o.optional_units} optional` : ''}`
}

const SAVE_PROBLEM = 'That did not save. Your previous setting is still in place.'

/**
 * Load + choose, shared by the pill and the block. Self-contained on purpose
 * (its own fetches, no shared service module) so either can be dropped into
 * any surface.
 */
export function useEnergyMode(onChanged?: () => void | Promise<void>) {
  const [data, setData] = useState<EnergyModeResponse | null>(null)
  const [saving, setSaving] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  // 503 = the rationing feature is off for this deployment. Render nothing
  // rather than a control that cannot work.
  const [unavailable, setUnavailable] = useState(false)

  const load = useCallback(async () => {
    try {
      const resp = await fetch(ENDPOINT, { credentials: 'include' })
      if (resp.status === 503) {
        setUnavailable(true)
        return
      }
      if (!resp.ok) return
      const body = (await resp.json()) as EnergyModeResponse
      if (body?.success && Array.isArray(body.modes)) setData(body)
    } catch {
      /* leave the control unrendered rather than guess at a mode */
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const choose = useCallback(
    async (mode: string) => {
      setProblem(null)
      setSaving(mode)
      try {
        const resp = await fetch(ENDPOINT, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode }),
        })
        if (!resp.ok) {
          setProblem(SAVE_PROBLEM)
          return
        }
        await load()
        await onChanged?.()
      } catch {
        setProblem(SAVE_PROBLEM)
      } finally {
        setSaving(null)
      }
    },
    [load, onChanged],
  )

  return { data, saving, problem, unavailable, choose }
}

/** What the rung does, in one sentence — never more than the server confirms. */
function consequenceLine(governsPlanSize: boolean | undefined): string {
  if (governsPlanSize === true) return 'Your call. It sets how much lands under Do, and how many Stretch slots open.'
  return 'Your call. It sets how many Stretch slots open today.'
}

/** "Today only. Back to Average tomorrow." — the fallback is the server's,
 *  not an assumed "usual": the baseline rung has no setter yet, so on most
 *  accounts the day falls back to the default rung by name. */
function todayOnlyLine(d: EnergyModeResponse): string {
  const fallback = d.baseline_mode ?? 'average'
  return `Today only. Back to ${labelFor(fallback)} tomorrow.`
}

/**
 * The masthead pill. Reads "Up for · Average" (quieter when nothing has been
 * chosen and Average is merely the rung in force), opens the five rungs with
 * their units, and says what choosing does.
 */
export function EnergyModePill({
  onChanged, className,
}: { onChanged?: () => void | Promise<void>; className?: string }) {
  const { data, saving, problem, unavailable, choose } = useEnergyMode(onChanged)
  if (unavailable || !data) return null
  const selected = data.mode ?? data.effective_mode
  const unset = data.mode === null
  const footnote = [
    unset ? `Nothing set today — ${labelFor(data.effective_mode)} is what today is running on.` : null,
    todayOnlyLine(data),
    problem,
  ].filter(Boolean).join(' ')
  return (
    <PillSelect
      className={className}
      prefix="Up for"
      value={labelFor(selected)}
      unset={unset}
      icon={<Sparkles className="h-3.5 w-3.5" />}
      question="How much are you up for today?"
      hint={consequenceLine(data.governs_plan_size)}
      options={data.modes.map(o => ({ value: o.value, label: labelFor(o.value), detail: unitsWords(o) }))}
      selected={selected}
      onSelect={choose}
      footnote={footnote}
      busy={saving !== null}
      tone="brand"
    />
  )
}

/** The long form: five buttons with units, for surfaces with room. */
export function EnergyModeControl({
  className, onChanged,
}: { className?: string; onChanged?: () => void | Promise<void> }) {
  const { data, saving, problem, unavailable, choose } = useEnergyMode(onChanged)
  if (unavailable || !data) return null

  const selected = data.mode ?? data.effective_mode

  return (
    <section
      className={className}
      aria-label="Energy mode for today"
    >
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
        How much are you up for today?
      </h3>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        {consequenceLine(data.governs_plan_size)}
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        {data.modes.map((option) => {
          const isSelected = option.value === selected
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => void choose(option.value)}
              disabled={saving !== null}
              aria-pressed={isSelected}
              className={[
                // >= 44px touch target.
                'min-h-[44px] min-w-[44px] rounded-lg border px-3 py-2 text-left',
                'transition-colors disabled:opacity-60',
                isSelected
                  ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950'
                  : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800',
              ].join(' ')}
            >
              {/* The name is what the user must recognise, so it never reads
                  smaller than the narration around it. */}
              <span className="block text-base font-medium text-gray-900 dark:text-gray-100">
                {labelFor(option.value)}
              </span>
              <span className="block text-sm text-gray-600 dark:text-gray-400">
                {unitsWords(option)}
              </span>
            </button>
          )
        })}
      </div>

      {data.mode === null && (
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          You haven&apos;t set one today — {labelFor(data.effective_mode)} is what
          today is running on.
        </p>
      )}

      {/* The route always stamps an expiry at the user's next local midnight,
          so this is a description of what choosing actually does. */}
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        {todayOnlyLine(data)}
      </p>

      {problem && (
        <p className="mt-2 text-sm text-amber-700 dark:text-amber-400" role="status">
          {problem}
        </p>
      )}
    </section>
  )
}

export default EnergyModeControl
